import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { FileText, Loader2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

export default function ScrapedContentDisplay({ content, onScrape, scraping, pageStructure }) {
  const [viewMode, setViewMode] = useState('groups');
  
  const isEmpty = content.length === 0 && !pageStructure?.blocks?.length;
  const hasStructure = pageStructure?.sections?.length > 0 && pageStructure?.blocks?.length > 0;
  const hasGroups = pageStructure?.groups?.length > 0;
  
  if (isEmpty) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="mb-2">No content scraped yet</p>
        <Button onClick={onScrape} disabled={scraping} variant="outline" size="sm">
          {scraping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Scan Website
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {hasStructure && <ViewToggle viewMode={viewMode} onChange={setViewMode} hasGroups={hasGroups} />}
      <div className="max-h-[550px] overflow-y-auto pr-2">
        {viewMode === 'groups' && hasGroups
          ? <GroupedView data={pageStructure} />
          : viewMode === 'structure' && hasStructure 
          ? <StructuredView data={pageStructure} />
          : <FlatView items={content} />
        }
      </div>
    </div>
  );
}

function ViewToggle({ viewMode, onChange, hasGroups }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-border">
      <span className="text-xs text-muted-foreground">View:</span>
      {hasGroups && <ToggleButton active={viewMode === 'groups'} onClick={() => onChange('groups')} label="Content Groups" />}
      <ToggleButton active={viewMode === 'structure'} onClick={() => onChange('structure')} label="By Section" />
      <ToggleButton active={viewMode === 'flat'} onClick={() => onChange('flat')} label="By Type" />
    </div>
  );
}

function ToggleButton({ active, onClick, label }) {
  const cls = active 
    ? 'bg-primary text-white' 
    : 'bg-muted text-muted-foreground hover:text-foreground';
  return (
    <button onClick={onClick} className={`text-xs px-3 py-1 rounded-full transition-colors ${cls}`}>
      {label}
    </button>
  );
}

function StructuredView({ data }) {
  const { sections, blocks } = data;
  const bySection = groupBlocksBySection(blocks);
  
  return (
    <div className="space-y-4">
      {sections.map((s, i) => <SectionCard key={s.section_id || i} section={s} blocks={bySection[s.section_id] || []} />)}
      {bySection['global']?.length > 0 && <GlobalSection blocks={bySection['global']} />}
    </div>
  );
}

function GroupedView({ data }) {
  const { sections, blocks, groups, elements } = data;
  const blockMap = Object.fromEntries(blocks.map(b => [b.block_id, b]));
  const elementMap = Object.fromEntries((elements || []).map(e => [e.element_id, e]));
  
  // Organize groups by section
  const sectionGroups = {};
  groups.forEach(g => {
    const sid = g.section_id || 'global';
    if (!sectionGroups[sid]) sectionGroups[sid] = [];
    sectionGroups[sid].push(g);
  });
  
  // Get ungrouped blocks by section
  const groupedBlockIds = new Set();
  groups.forEach(g => {
    if (g.heading_block_id) groupedBlockIds.add(g.heading_block_id);
    (g.child_block_ids || []).forEach(id => groupedBlockIds.add(id));
    (g.description_block_ids || []).forEach(id => groupedBlockIds.add(id));
  });
  
  const ungroupedBySection = {};
  blocks.forEach(b => {
    if (groupedBlockIds.has(b.block_id)) return;
    const sid = b.section_id || 'global';
    if (!ungroupedBySection[sid]) ungroupedBySection[sid] = [];
    ungroupedBySection[sid].push(b);
  });
  
  // Get CTAs by section (not in groups)
  const groupedCtaIds = new Set();
  groups.forEach(g => (g.cta_element_ids || []).forEach(id => groupedCtaIds.add(id)));
  
  const ungroupedCtasBySection = {};
  (elements || []).forEach(e => {
    if (!e.is_cta && e.element_type !== 'primary_cta' && e.element_type !== 'secondary_cta') return;
    if (groupedCtaIds.has(e.element_id)) return;
    const sid = e.section_id || 'global';
    if (!ungroupedCtasBySection[sid]) ungroupedCtasBySection[sid] = [];
    ungroupedCtasBySection[sid].push(e);
  });
  
  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const secGroups = sectionGroups[section.section_id] || [];
        const secUngrouped = ungroupedBySection[section.section_id] || [];
        const secCtas = ungroupedCtasBySection[section.section_id] || [];
        
        if (secGroups.length === 0 && secUngrouped.length === 0 && secCtas.length === 0) return null;
        
        return (
          <SectionGroupCard 
            key={section.section_id} 
            section={section} 
            groups={secGroups}
            ungrouped={secUngrouped}
            ungroupedCtas={secCtas}
            blockMap={blockMap}
            elementMap={elementMap}
          />
        );
      })}
    </div>
  );
}

function SectionGroupCard({ section, groups, ungrouped, ungroupedCtas, blockMap, elementMap }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const label = section.inferred_label || getSectionMeta(section.section_type).label;
  const color = getSectionColor(section.section_type);
  const ctaCount = (ungroupedCtas || []).length + groups.reduce((acc, g) => acc + (g.cta_element_ids || []).length, 0);
  const totalItems = groups.length + ungrouped.length + ctaCount;
  
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div 
        className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className={`text-xs font-semibold px-2 py-1 rounded ${color}`}>{label}</span>
          {section.above_the_fold && <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">Above Fold</span>}
          {ctaCount > 0 && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">{ctaCount} CTAs</span>}
        </div>
        <span className="text-xs text-muted-foreground">{groups.length} groups, {totalItems} items</span>
      </div>
      
      {isExpanded && (
        <div className="p-3 space-y-3">
          {groups.map((group, idx) => (
            <ContentGroupCard key={group.group_id || idx} group={group} blockMap={blockMap} elementMap={elementMap} />
          ))}
          {ungrouped.length > 0 && (
            <div className="pt-2 border-t border-border/50">
              <span className="text-xs text-muted-foreground mb-2 block">Standalone content</span>
              <div className="space-y-1">
                {ungrouped.map((b, i) => <BlockItem key={b.block_id || i} item={b} />)}
              </div>
            </div>
          )}
          {(ungroupedCtas || []).length > 0 && (
            <div className="pt-2 border-t border-border/50">
              <span className="text-xs text-muted-foreground mb-2 block">CTAs in this section</span>
              <div className="space-y-1">
                {ungroupedCtas.map((cta, i) => <CtaItem key={cta.element_id || i} cta={cta} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContentGroupCard({ group, blockMap, elementMap }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const heading = group.heading_block_id ? blockMap[group.heading_block_id] : null;
  const children = (group.child_block_ids || []).map(id => blockMap[id]).filter(Boolean);
  const descriptions = (group.description_block_ids || []).map(id => blockMap[id]).filter(Boolean);
  const ctas = (group.cta_element_ids || []).map(id => elementMap ? elementMap[id] : null).filter(Boolean);
  
  const groupTypeLabel = getGroupTypeLabel(group.group_type);
  const groupColor = getGroupTypeColor(group.group_type);
  
  return (
    <div className="bg-muted/20 rounded-lg border border-border/50 overflow-hidden">
      <div 
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${groupColor}`}>{groupTypeLabel}</span>
          {heading && (
            <span className="text-sm font-medium text-foreground truncate max-w-[300px]">
              {heading.text}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {ctas.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{ctas.length} CTAs</span>}
          <span className="text-[10px] text-muted-foreground">
            {children.length} items
          </span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          {children.map((child, i) => (
            <div key={child.block_id || i} className="flex items-start gap-2 pl-5">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase shrink-0">
                {child.heading_level || child.block_type}
              </span>
              <span className="text-sm text-foreground">{child.text}</span>
            </div>
          ))}
          {descriptions.length > 0 && (
            <div className="pl-5 pt-1 space-y-1">
              {descriptions.map((desc, i) => (
                <p key={desc.block_id || i} className="text-xs text-muted-foreground">
                  {desc.text}
                </p>
              ))}
            </div>
          )}
          {ctas.length > 0 && (
            <div className="pl-5 pt-2 border-t border-border/30 mt-2 space-y-1">
              {ctas.map((cta, i) => <CtaItem key={cta.element_id || i} cta={cta} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CtaItem({ cta }) {
  const isPrimary = cta.element_type === 'primary_cta';
  const bgColor = isPrimary ? 'bg-purple-100' : 'bg-purple-50';
  const textColor = isPrimary ? 'text-purple-700' : 'text-purple-600';
  
  return (
    <div className={`flex items-center gap-2 p-2 rounded ${bgColor}`}>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-200 ${textColor} uppercase shrink-0`}>
        {isPrimary ? 'Primary CTA' : 'CTA'}
      </span>
      <span className={`text-sm font-medium ${textColor}`}>{cta.visible_text}</span>
      {cta.href && (
        <a 
          href={cta.href} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-purple-500 hover:underline truncate max-w-[200px]"
          onClick={(e) => e.stopPropagation()}
        >
          → {cta.href.replace(/^https?:\/\//, '').slice(0, 30)}...
        </a>
      )}
    </div>
  );
}

function getGroupTypeLabel(type) {
  const labels = {
    'feature_list': 'Features',
    'feature_card_group': 'Cards',
    'faq_list': 'FAQ',
    'step_card_group': 'Steps',
    'pricing_card_group': 'Pricing',
    'benefit_card_group': 'Benefits',
  };
  return labels[type] || 'Group';
}

function getGroupTypeColor(type) {
  const colors = {
    'feature_list': 'bg-blue-100 text-blue-700',
    'feature_card_group': 'bg-cyan-100 text-cyan-700',
    'faq_list': 'bg-sky-100 text-sky-700',
    'step_card_group': 'bg-indigo-100 text-indigo-700',
    'pricing_card_group': 'bg-emerald-100 text-emerald-700',
    'benefit_card_group': 'bg-teal-100 text-teal-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
}

function groupBlocksBySection(blocks) {
  const result = {};
  blocks.forEach(b => {
    const sid = b.section_id || 'global';
    if (!result[sid]) result[sid] = [];
    result[sid].push(b);
  });
  return result;
}

function SectionCard({ section, blocks }) {
  // Skip empty nav sections
  if (blocks.length === 0 && section.section_type === 'nav') return null;
  
  // Use inferred_label from backend if available, otherwise fall back to getSectionMeta
  const label = section.inferred_label || getSectionMeta(section.section_type).label;
  const color = getSectionColor(section.section_type);
  
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <SectionHeader section={section} label={label} color={color} count={blocks.length} />
      {blocks.length > 0 && <BlocksList blocks={blocks} />}
    </div>
  );
}

function SectionHeader({ section, label, color, count }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border">
      <div className="flex items-center gap-2">
        <span className={`text-xs font-semibold px-2 py-1 rounded ${color}`}>{label}</span>
        {section.above_the_fold && <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">Above Fold</span>}
        <span className="text-xs text-muted-foreground">&lt;{section.html_tag}&gt;</span>
      </div>
      <span className="text-xs text-muted-foreground">{count} items</span>
    </div>
  );
}

function GlobalSection({ blocks }) {
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="px-4 py-3 bg-muted/30 border-b border-border">
        <span className="text-xs font-semibold text-muted-foreground">Other Content ({blocks.length} items)</span>
      </div>
      <BlocksList blocks={blocks} />
    </div>
  );
}

function BlocksList({ blocks }) {
  return (
    <div className="p-3 space-y-2">
      {blocks.map((b, i) => <BlockItem key={b.block_id || i} item={b} />)}
    </div>
  );
}

function BlockItem({ item }) {
  const color = getBlockColor(item.block_type);
  return (
    <div className="p-2 bg-background rounded-lg border border-border/50 hover:border-border transition-colors">
      <div className="flex items-start gap-2">
        <span className="text-xs font-medium text-muted-foreground uppercase shrink-0 w-16">
          {item.heading_level || item.block_type.replace('_', ' ')}
        </span>
        <p className={`text-sm ${color} flex-1`}>{item.text}</p>
      </div>
    </div>
  );
}

function FlatView({ items }) {
  const groups = [
    { key: 'hero', label: 'Hero Section', data: items.filter(s => s.section_type === 'hero') },
    { key: 'heading', label: 'Headings', data: items.filter(s => s.section_type === 'heading') },
    { key: 'body', label: 'Body Content', data: items.filter(s => s.section_type === 'body') },
    { key: 'cta', label: 'Call-to-Actions', data: items.filter(s => s.section_type === 'cta') }
  ];
  
  return (
    <div className="space-y-6">
      {groups.map(g => g.data.length > 0 && <ContentGroup key={g.key} label={g.label} items={g.data} />)}
    </div>
  );
}

function ContentGroup({ label, items }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3 flex items-center gap-2">
        {label} <span className="text-primary bg-primary/10 px-2 py-0.5 rounded-full">{items.length}</span>
      </h4>
      <div className="space-y-2">
        {items.map(s => <ContentItem key={s.id} item={s} />)}
      </div>
    </div>
  );
}

function ContentItem({ item }) {
  return (
    <div className="p-3 bg-muted/30 rounded-lg border border-border/50">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs text-muted-foreground">{item.tag || 'unknown'}</span>
      </div>
      <p className="text-sm text-foreground whitespace-pre-line">{item.content}</p>
    </div>
  );
}

// Color mapping for semantic section types
function getSectionColor(type) {
  const colors = {
    // Navigation & Structure
    header: 'bg-slate-100 text-slate-700',
    nav: 'bg-slate-100 text-slate-600',
    footer: 'bg-slate-100 text-slate-600',
    
    // Hero & Primary
    hero: 'bg-purple-100 text-purple-700',
    cta_section: 'bg-purple-100 text-purple-700',
    
    // Features & Benefits
    features: 'bg-blue-100 text-blue-700',
    benefits: 'bg-cyan-100 text-cyan-700',
    value_props: 'bg-indigo-100 text-indigo-700',
    
    // Process & How It Works
    how_it_works: 'bg-teal-100 text-teal-700',
    
    // Social Proof & Trust
    social_proof: 'bg-amber-100 text-amber-700',
    logos: 'bg-amber-50 text-amber-600',
    authority: 'bg-amber-100 text-amber-700',
    
    // Pricing & Comparison
    pricing: 'bg-emerald-100 text-emerald-700',
    comparison: 'bg-green-100 text-green-700',
    
    // Risk & FAQ
    risk_reversal: 'bg-orange-100 text-orange-700',
    faq: 'bg-sky-100 text-sky-700',
    
    // Other
    banner: 'bg-red-100 text-red-700',
    modal: 'bg-orange-100 text-orange-700',
    aside: 'bg-yellow-100 text-yellow-700',
    content: 'bg-gray-100 text-gray-700',
    unknown: 'bg-gray-100 text-gray-600',
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
}

// Fallback label mapping (used when inferred_label is not available)
function getSectionMeta(type) {
  const map = {
    header: { label: 'Header' },
    nav: { label: 'Navigation' },
    footer: { label: 'Footer' },
    hero: { label: 'Hero' },
    cta_section: { label: 'CTA Section' },
    features: { label: 'Features' },
    benefits: { label: 'Benefits' },
    value_props: { label: 'Value Propositions' },
    how_it_works: { label: 'How It Works' },
    social_proof: { label: 'Social Proof' },
    logos: { label: 'Client Logos' },
    authority: { label: 'Trust/Authority' },
    pricing: { label: 'Pricing' },
    comparison: { label: 'Comparison' },
    risk_reversal: { label: 'Risk Reversal' },
    faq: { label: 'FAQ' },
    banner: { label: 'Banner' },
    modal: { label: 'Modal' },
    aside: { label: 'Sidebar' },
    content: { label: 'Content' },
    unknown: { label: 'Section' },
  };
  return map[type] || { label: type || 'Section' };
}

function getBlockColor(type) {
  const map = {
    // Text content
    headline: 'text-blue-600 font-semibold',
    subheadline: 'text-blue-500',
    paragraph: 'text-foreground',
    list: 'text-foreground',
    bullet_point: 'text-foreground',
    
    // Cards/Components
    feature_card: 'text-cyan-600',
    benefit_card: 'text-teal-600',
    testimonial: 'text-amber-600',
    review: 'text-amber-500',
    pricing_card: 'text-emerald-600',
    step_card: 'text-indigo-600',
    faq_item: 'text-sky-600',
    
    // Interactive
    cta_group: 'text-purple-600 font-medium',
    cta_button: 'text-purple-500',
  };
  return map[type] || 'text-foreground';
}
